import * as satellite from 'satellite.js';
import { normLon, toDeg, toRad, type GeoPoint } from './geo';

export interface TrackedSat {
  name: string;
  norad: string;
  rec: satellite.SatRec;
}

export interface SatPosition {
  name: string;
  norad: string;
  lat: number;
  lon: number;
  heightKm: number;
  velocityKms: number;
  /** Azimuth from observer (degrees, 0 = N). NaN when no observer. */
  az: number;
  /** Elevation above observer horizon (degrees). NaN when no observer. */
  el: number;
  aboveHorizon: boolean;
  isIss: boolean;
  footprintKm: number;
}

const TLE_GROUPS = ['stations', 'visual', 'amateur'];
const EARTH_RADIUS_KM = 6371;

const celestrakUrl = (group: string) =>
  `https://celestrak.org/NORAD/elements/gp.php?GROUP=${group}&FORMAT=tle`;
const AMSAT_BULLETIN_URL = 'https://www.amsat.org/tle/daily-bulletin.txt';
const ivanApiUrl = (norad: number) => `https://tle.ivanstanojevic.me/api/tle/${norad}`;
const proxyUrl = (url: string) => `https://proxy.shakespeare.diy/?url=${encodeURIComponent(url)}`;

/** Notable satellites used as a last-resort fallback set. */
const CURATED_NORAD = [
  25544, // ISS (ZARYA)
  48274, // TIANHE (Chinese station core)
  20580, // Hubble Space Telescope
  25338, // NOAA 15
  28654, // NOAA 18
  33591, // NOAA 19
  29499, // METOP-A
  38771, // METOP-B
  43689, // METOP-C
  40069, // METEOR-M 2
  44387, // METEOR-M2 2
  39084, // SUOMI NPP
  43013, // NOAA 20
  54234, // NOAA 21
  27386, // ENVISAT
  39444, // AO-73 (FUNcube-1)
  40967, // SO-50
  43017, // AO-91
  43137, // AO-92
  44909, // RS-44 (DOSAAF-85)
  51069, // AO-109 (Fox-1E)
];

async function fetchText(url: string, signal?: AbortSignal): Promise<string> {
  const res = await fetch(url, { signal: signal ?? AbortSignal.timeout(15000) });
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
  return res.text();
}

function collectSats(texts: string[]): TrackedSat[] {
  const map = new Map<string, TrackedSat>();
  for (const text of texts) {
    for (const sat of parseTle(text)) {
      if (!map.has(sat.norad)) map.set(sat.norad, sat);
    }
  }
  return [...map.values()];
}

/** Fetch a single TLE from the ivanstanojevic API as 3-line text. */
async function fetchCuratedTle(norad: number, signal?: AbortSignal): Promise<string> {
  const urls = [ivanApiUrl(norad), proxyUrl(ivanApiUrl(norad))];
  let lastError: unknown = null;
  for (const url of urls) {
    try {
      const res = await fetch(url, { signal: signal ?? AbortSignal.timeout(12000) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = (await res.json()) as { name?: string; line1?: string; line2?: string };
      if (j.name && j.line1 && j.line2) return `${j.name}\n${j.line1}\n${j.line2}`;
      throw new Error('Malformed TLE JSON');
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError instanceof Error ? lastError : new Error('TLE fetch failed');
}

/**
 * Fetch TLE sets with a multi-source fallback chain:
 * CelesTrak groups (direct, then proxy) → AMSAT bulletin (direct, then proxy)
 * → curated NORAD list via the tle.ivanstanojevic.me API.
 */
export async function fetchTles(signal?: AbortSignal): Promise<TrackedSat[]> {
  // 1) CelesTrak groups: direct, then through the CORS proxy.
  for (const useProxy of [false, true]) {
    try {
      const texts = await Promise.all(
        TLE_GROUPS.map((group) =>
          fetchText(useProxy ? proxyUrl(celestrakUrl(group)) : celestrakUrl(group), signal),
        ),
      );
      const sats = collectSats(texts);
      if (sats.length >= 20) return sats;
    } catch {
      // try next variant
    }
  }

  // 2) AMSAT daily bulletin: direct, then through the proxy.
  for (const useProxy of [false, true]) {
    try {
      const text = await fetchText(useProxy ? proxyUrl(AMSAT_BULLETIN_URL) : AMSAT_BULLETIN_URL, signal);
      const sats = collectSats([text]);
      if (sats.length >= 10) return sats;
    } catch {
      // try next variant
    }
  }

  // 3) Curated NORAD list via the TLE API.
  const results = await Promise.allSettled(CURATED_NORAD.map((id) => fetchCuratedTle(id, signal)));
  const texts = results
    .filter((r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled')
    .map((r) => r.value);
  const sats = collectSats(texts);
  if (sats.length === 0) throw new Error('All TLE sources unavailable');
  return sats;
}

/** Parse 3-line TLE text into satellite records. */
export function parseTle(text: string): TrackedSat[] {
  const lines = text.split(/\r?\n/).map((l) => l.trimEnd());
  const out: TrackedSat[] = [];
  for (let i = 0; i <= lines.length - 3; i++) {
    const name = lines[i];
    const l1 = lines[i + 1];
    const l2 = lines[i + 2];
    if (
      l1?.startsWith('1 ') &&
      l2?.startsWith('2 ') &&
      name &&
      !name.startsWith('1 ') &&
      !name.startsWith('2 ') &&
      !name.toUpperCase().startsWith('DECAYED')
    ) {
      try {
        const rec = satellite.twoline2satrec(l1, l2);
        if (rec && !rec.error) {
          out.push({ name: name.trim(), norad: rec.satnum, rec });
        }
      } catch {
        // skip malformed TLE
      }
      i += 2;
    }
  }
  return out;
}

/** Propagate all tracked satellites to a moment in time. */
export function propagateSats(sats: TrackedSat[], date: Date, observer?: GeoPoint): SatPosition[] {
  const gmst = satellite.gstime(date);
  const obsGd = observer
    ? { latitude: toRad(observer.lat), longitude: toRad(observer.lon), height: 0 }
    : null;
  const out: SatPosition[] = [];

  for (const sat of sats) {
    try {
      const pv = satellite.propagate(sat.rec, date);
      const position = pv?.position;
      const velocity = pv?.velocity;
      if (!position || !velocity || typeof position !== 'object' || typeof velocity !== 'object') {
        continue;
      }
      if (
        ![position.x, position.y, position.z, velocity.x, velocity.y, velocity.z].every(
          Number.isFinite,
        )
      ) {
        continue;
      }

      const geo = satellite.eciToGeodetic(position, gmst);
      const lat = toDeg(geo.latitude);
      const lon = normLon(toDeg(geo.longitude));
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;

      let az = NaN;
      let el = NaN;
      if (obsGd) {
        const ecf = satellite.eciToEcf(position, gmst);
        const look = satellite.ecfToLookAngles(obsGd, ecf);
        az = toDeg(look.azimuth);
        if (az < 0) az += 360;
        el = toDeg(look.elevation);
      }

      const h = Math.max(geo.height, 1);
      out.push({
        name: sat.name,
        norad: sat.norad,
        lat,
        lon,
        heightKm: geo.height,
        velocityKms: Math.sqrt(velocity.x ** 2 + velocity.y ** 2 + velocity.z ** 2),
        az,
        el,
        aboveHorizon: Number.isFinite(el) && el > 0,
        isIss: sat.norad === '25544',
        footprintKm: Math.acos(EARTH_RADIUS_KM / (EARTH_RADIUS_KM + h)) * EARTH_RADIUS_KM,
      });
    } catch {
      // decayed or invalid — skip
    }
  }
  return out;
}
